/**
 * CallTrackingMetrics Wire-Format Transformers (Story 22.3, AC #4, AC #12)
 *
 * CTM's API returns snake_case field names (`talk_time`, `caller_number`,
 * `tracking_number_format`, etc.). ProvenLeap's record schema uses
 * lowerCamelCase consistently across all adapters. The transformers
 * here rewrite the wire-format records to the ProvenLeap shape per
 * endpoint type, applying THREE defensive layers along the way:
 *
 *  1. **`stripCsvInjectionPrefix` on every string-valued field** that
 *     could end up in a CSV export (Round 3 Red Team Attack #4). Leading
 *     `=`, `+`, `@`, `-`, tab, CR, LF characters are LOOP-stripped to
 *     defend against pathological customer-supplied data like
 *     `name = "=cmd|/c calc!A1"` triggering Excel formula execution
 *     on export.
 *
 *  2. **Audio (recording) URL stripping** on every call record (Round 3
 *     Red Team Attack #2). CTM's `audio` field is a signed URL — if a
 *     buggy widget echoed this cross-tenant, it would be a data leak.
 *     Story 22.3 deliberately does NOT persist audio URLs; widgets that
 *     need them request via a dedicated tenant-scoped route (deferred
 *     to Story 22.5). The transformer strips `audio` from every call
 *     record before return.
 *
 *  3. **Timezone normalization to UTC** on every timestamp field (Round 5
 *     Edge Case 5). Per the catalogue line 301: *"`called_at` is returned
 *     in account timezone with offset, not UTC. Normalize to UTC in the
 *     adapter."* The transformer ALWAYS converts `called_at` /
 *     `created_at` to a UTC ISO 8601 string and emits the normalized
 *     value under `calledAtUtc` / `createdAtUtc` (preserving the
 *     original under `calledAt` / `createdAt` for forensic compat).
 *
 * **`_reportType` discriminator (per AC #4):** every record carries a
 * `_reportType` literal identifying which endpoint produced it. Mirrors
 * the CallRail 22.1 / Ahrefs 20.1 / Semrush 20.4 / Moz 20.7 pattern —
 * downstream consumers use this to switch on record shape.
 */

import { stripCsvInjectionPrefix } from "./pii";

/**
 * The `_reportType` literals CTM emits. Frozen union —
 * adding a new endpoint requires both adding a new transformer here
 * AND extending this type.
 *
 * `"derived-aggregates"` is emitted by the ctm.ts adapter itself
 * (not by the per-endpoint transformers) — it carries the 8 Call
 * Volume + 6 Inline-Sale + 5 SMS + 3 Form-Submission derived
 * metrics in a SINGLE record per fetchMetrics call. Downstream
 * widget renderers switching on the union MUST handle this case
 * (for KPI / gauge widgets that bind to aggregate metric keys).
 *
 * **Story 35.4 widens the union to 7 literals** by adding `"triggers"`
 * (LeadReactor configurations from `/triggers.json`) and `"integrations"`
 * (3rd-party partner connections from `/integrations.json`).
 */
export type CtmReportType =
  | "calls-list"
  | "form-submissions"
  | "sms"
  | "accounts-list"
  | "triggers"
  | "integrations"
  | "derived-aggregates";

// ============================================================================
// snake_case → lowerCamelCase keys
// ============================================================================

/**
 * Convert a snake_case string to lowerCamelCase. Pure function.
 * `talk_time` → `talkTime`, `caller_number` → `callerNumber`.
 *
 * Leaves already-camelCase strings unchanged. Handles mixed-shape
 * inputs.
 */
function snakeToCamel(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase());
}

/**
 * Reserved JS-runtime keys — same defense as `pii.ts FORBIDDEN_KEYS`.
 * Skip them entirely so `out[key] = value` cannot mutate
 * `Object.prototype` or confuse downstream consumers that perform
 * property-existence checks.
 */
const FORBIDDEN_SANITIZE_KEYS: ReadonlySet<string> = new Set([
  "__proto__",
  "constructor",
  "prototype",
]);

/**
 * Apply CSV-injection sanitization to every string-valued field in a
 * record. Recurses into arrays of strings (e.g. `tag_list`) and into
 * nested-object fields (e.g. `paid: { campaign, source, medium }`).
 * Other types pass through unchanged. Pure function — returns a new
 * shallow copy.
 *
 * Depth cap 6 — CTM nested shapes like `webvisit.location_*` naturally
 * hit depth 3-4; depth 6 leaves room without admitting pathological
 * inputs.
 */
function sanitizeStringsImpl(value: unknown, depth: number): unknown {
  if (typeof value === "string") {
    return stripCsvInjectionPrefix(value);
  }
  if (depth >= 6 || value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeStringsImpl(item, depth + 1));
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (FORBIDDEN_SANITIZE_KEYS.has(k)) continue;
      out[k] = sanitizeStringsImpl(v, depth + 1);
    }
    return out;
  }
  return value;
}

function sanitizeStrings<T extends Record<string, unknown>>(
  record: T,
): Record<string, unknown> {
  return sanitizeStringsImpl(record, 0) as Record<string, unknown>;
}

/**
 * Strip ANY field whose key contains `audio` OR `recording` (case-insensitive)
 * from a record. Story 22.3 does NOT persist audio URLs; Story 22.5 widens
 * this to a regex-based whitelist filter so future endpoint families OR
 * schema drift cannot re-introduce upstream URLs.
 *
 * Round 3 Red Team Attack #2 defense (Story 22.3) + Story 22.5 AC #8/#21
 * cradle-to-grave invariant. The `recording` regex covers CallRail-style
 * keys that might appear in CTM responses too (defense-in-depth).
 */
function stripAudioUrls(
  record: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (/audio/i.test(key)) continue;
    if (/recording/i.test(key)) continue;
    out[key] = value;
  }
  return out;
}

/**
 * Normalize a CTM timestamp string to a UTC ISO 8601 string. CTM
 * returns `called_at` in account timezone (per the catalogue line 301);
 * the transformer ALWAYS converts to UTC.
 *
 * Inputs:
 *  - ISO 8601 with offset: `"2026-05-15T14:30:00-05:00"` → `"2026-05-15T19:30:00.000Z"`
 *  - UTC ISO: `"2026-05-15T19:30:00Z"` → `"2026-05-15T19:30:00.000Z"`
 *  - Unix epoch (numeric or string): `1747333800` → `"2026-05-15T19:30:00.000Z"`
 *  - Empty / null / malformed → `null` (caller decides whether to log
 *    `ctm_timezone_missing`)
 *
 * Pure function. Returns `null` on un-parseable inputs rather than
 * throwing so the transformer doesn't crash on a malformed row from
 * the wire.
 */
export function normalizeCtmTimestampToUtc(
  value: unknown,
): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    // CTM `unix_time` field is documented as seconds-since-epoch per
    // the Postman collection observed 2026-05-15. Multiply by 1000.
    const ms = value > 1e12 ? value : value * 1000;
    const d = new Date(ms);
    if (!Number.isFinite(d.getTime())) return null;
    return d.toISOString();
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const parsed = Date.parse(trimmed);
    if (!Number.isFinite(parsed)) return null;
    return new Date(parsed).toISOString();
  }
  return null;
}

// ============================================================================
// Per-endpoint transformers
// ============================================================================

/**
 * Wire-format shape for `/calls.json` `calls[i]` rows.
 *
 * Loose `Record` typing because CTM's full field set is broad and the
 * docs don't expose a formal schema.
 */
export type CtmCallApiRow = Record<string, unknown>;

/**
 * Transform a single row from `/calls.json`. STRIPS `audio` URLs per
 * Round 3 Red Team Attack #2 — audio URLs never enter the persisted
 * record shape in Story 22.3.
 *
 * **PII is NOT redacted here** — the transformer is on the
 * `fetchMetrics` happy-path BEFORE records flow into the tenant-scoped
 * KV cache + D1 persistence. PII redaction happens at the LOG boundary
 * via `sanitizeCtmPii`. This separation is deliberate: tenant-scoped
 * persistence keeps PII secure, but log-output (which spans tenants
 * forensically) must redact.
 *
 * Timezone normalization: emits BOTH the original `calledAt` (forensic
 * compatibility — preserve the offset-laden value as-returned) AND a
 * normalized `calledAtUtc` derived via `normalizeCtmTimestampToUtc`.
 * The downstream consumer should prefer `calledAtUtc` for any
 * time-bucket calculation.
 */
export function transformCtmCallRow(
  row: CtmCallApiRow,
  context: {
    readonly accountId: string;
  },
): Record<string, unknown> {
  const stripped = stripAudioUrls(row);
  const camelKeyed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(stripped)) {
    if (FORBIDDEN_SANITIZE_KEYS.has(key)) continue;
    camelKeyed[snakeToCamel(key)] = value;
  }
  const sanitized = sanitizeStrings(camelKeyed);

  // Timezone normalization (Round 5 Edge Case 5).
  const calledAtUtc = normalizeCtmTimestampToUtc(
    sanitized["calledAt"] ?? sanitized["unixTime"],
  );

  // Story 35.4 AC #9 — explicit CI-gated expansion fields (also
  // automatically named via `snakeToCamel`, but listed here for
  // discoverability + to anchor the contract on the transformer rather
  // than the aggregator). The fields are PII-flagged for log emissions
  // via `sanitizeCtmPii`'s flat-list + nested-array shape handlers.
  //   `spotted`         — array of detected keywords (CI-gated)
  //   `notes`           — free-text agent notes (CI-gated, PII-bearing)
  //   `callPath`        — array of {route_name, route_id, route_type, started_at}
  //   `firstTouch`      — URL string (CI-gated, PII may embed in query)
  //   `lastTouch`       — URL string (CI-gated)
  //   `lastLocation`    — URL string (CI-gated)
  //   `scorecards`      — array of {id, name, score, passed, sections}
  //   `agentInsights`   — boolean AI flag (Story 22.3 baseline; re-validated)
  // No explicit re-mapping needed — `snakeToCamel` produces the
  // expected camelCase names from the wire-format snake_case. This
  // block exists to make the contract visible to readers + downstream
  // build-time checkers.

  return {
    _reportType: "calls-list",
    accountId: context.accountId,
    ...sanitized,
    ...(calledAtUtc ? { calledAtUtc } : {}),
  };
}

/**
 * Wire-format shape for `/forms.json` `form_submissions[i]` rows.
 */
export type CtmFormSubmissionApiRow = Record<string, unknown>;

/**
 * Transform a single row from `/forms.json`. Same PII / timezone
 * contract as the calls transformer (PII flows through for persistence;
 * logging gate redacts).
 *
 * Form-submission timestamps use `created_at` (NOT `called_at`).
 *
 * **Story 35.4 AC #9 — explicit Forms expansion field mapping.** CTM's
 * `/forms.json` returns the new Batch-1 expansion fields EITHER at the
 * top level OR nested under a `form` object (the public schema varies
 * by API version). To give downstream aggregators a stable shape, the
 * transformer hoists 8 NEW fields to top-level lowerCamelCase keys —
 * preferring top-level if both present, falling back to nested `form.*`
 * otherwise. The hoisted names match what `aggregateFormSubmissions`
 * reads at `ctm.ts`:
 *  - `formCustomFields`  ← `custom` (top) OR `form.custom` (nested)
 *  - `formId`            ← `form_id` OR `form.form_id`
 *  - `formName`          ← `form_name` OR `form.form_name`
 *  - `formDuration`      ← `duration` OR `form.duration`
 *  - `formCompleted`     ← `completed` OR `form.completed`
 *  - `formTriggeredBy`   ← `triggered_by` OR `form.triggered_by`
 *  - `formFirstTouch`    ← `first_touch` OR `form.first_touch`
 *  - `formLastTouch`     ← `last_touch` OR `form.last_touch`
 */
export function transformCtmFormSubmissionRow(
  row: CtmFormSubmissionApiRow,
  context: {
    readonly accountId: string;
  },
): Record<string, unknown> {
  const camelKeyed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (FORBIDDEN_SANITIZE_KEYS.has(key)) continue;
    camelKeyed[snakeToCamel(key)] = value;
  }
  const sanitized = sanitizeStrings(camelKeyed);

  const createdAtUtc = normalizeCtmTimestampToUtc(sanitized["createdAt"]);

  // Story 35.4 AC #9 — hoist Batch-1 Forms expansion fields from
  // EITHER top-level OR nested `form.*` to stable lowerCamelCase keys
  // that `aggregateFormSubmissions` reads. The `form` nested object is
  // emitted by some CTM API versions; the helper accepts both shapes.
  const formObj =
    sanitized["form"] && typeof sanitized["form"] === "object"
      ? (sanitized["form"] as Record<string, unknown>)
      : null;
  const pickFormField = (
    topLevel: string,
    nestedSnake: string,
  ): unknown => {
    const topVal = sanitized[topLevel];
    if (topVal !== undefined && topVal !== null) return topVal;
    // **Round 2 fix — symmetric null handling.** Treat nested `null`
    // the same as top-level `null` (both "missing"). Previously the
    // helper emitted `formCompleted: null` when nested was explicitly
    // null, breaking downstream `typeof === "boolean"` consumers.
    if (formObj) {
      const nestedVal = formObj[nestedSnake];
      if (nestedVal !== undefined && nestedVal !== null) return nestedVal;
    }
    return undefined;
  };
  const formCustomFields = pickFormField("custom", "custom");
  const formId = pickFormField("formId", "form_id");
  const formName = pickFormField("formName", "form_name");
  const formDuration = pickFormField("duration", "duration");
  const formCompleted = pickFormField("completed", "completed");
  const formTriggeredBy = pickFormField("triggeredBy", "triggered_by");
  const formFirstTouch = pickFormField("firstTouch", "first_touch");
  const formLastTouch = pickFormField("lastTouch", "last_touch");

  return {
    _reportType: "form-submissions",
    accountId: context.accountId,
    ...sanitized,
    ...(createdAtUtc ? { createdAtUtc } : {}),
    // AC #9 — explicit hoisted names. Only emit when defined to avoid
    // poisoning the record with `undefined`-valued keys.
    ...(formCustomFields !== undefined ? { formCustomFields } : {}),
    ...(formId !== undefined ? { formId } : {}),
    ...(formName !== undefined ? { formName } : {}),
    ...(formDuration !== undefined ? { formDuration } : {}),
    ...(formCompleted !== undefined ? { formCompleted } : {}),
    ...(formTriggeredBy !== undefined ? { formTriggeredBy } : {}),
    ...(formFirstTouch !== undefined ? { formFirstTouch } : {}),
    ...(formLastTouch !== undefined ? { formLastTouch } : {}),
    // **Round 3 fix:** also hoist `triggeredBy` (Story 22.3 baseline
    // contract) when nested-form shape is the only source. Without this
    // hoist, `aggregateFormSubmissions`' `if (r["triggeredBy"] ===
    // "form_reactor")` check undercounts when CTM uses the nested
    // `form.triggered_by` shape.
    ...(formTriggeredBy !== undefined && sanitized["triggeredBy"] === undefined
      ? { triggeredBy: formTriggeredBy }
      : {}),
  };
}

/**
 * Wire-format shape for `/sms.json` SMS message rows. Note: CTM's
 * Postman collection observed 2026-05-15 uses either `sms` or
 * `messages` as the response array key depending on the endpoint
 * variant — the API helper handles both defensively, and the
 * transformer is shape-agnostic.
 */
export type CtmSmsApiRow = Record<string, unknown>;

/**
 * Transform a single row from `/sms.json`. SMS bodies are
 * user-controlled content (the caller types the message) so the
 * `message` field is BOTH PII-flagged (for log redaction) AND
 * CSV-injection-sanitized (for export safety).
 *
 * **Story 35.4 expansion:** the SMS shape gains per-message timestamps
 * (`sent_time` / `received_time`), thread/conversation IDs, delivery
 * status, and SMS-level attribution (`source` / `medium` / `campaign`).
 * The `snakeToCamel` walker handles the new fields automatically;
 * timestamps for the new fields normalize via the same
 * `normalizeCtmTimestampToUtc` path.
 */
export function transformCtmSmsRow(
  row: CtmSmsApiRow,
  context: {
    readonly accountId: string;
  },
): Record<string, unknown> {
  const camelKeyed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (FORBIDDEN_SANITIZE_KEYS.has(key)) continue;
    camelKeyed[snakeToCamel(key)] = value;
  }
  const sanitized = sanitizeStrings(camelKeyed);

  const createdAtUtc = normalizeCtmTimestampToUtc(sanitized["createdAt"]);
  // Story 35.4 — Batch 2: granular per-message timestamps. Normalize to
  // UTC alongside createdAt.
  const sentTimeUtc = normalizeCtmTimestampToUtc(sanitized["sentTime"]);
  const receivedTimeUtc = normalizeCtmTimestampToUtc(sanitized["receivedTime"]);
  // Story 35.4 — Batch 2: thread_id preferred over conversation_id
  // when both present; synthetic-derived from caller_number if neither
  // (signal preserved as `threadId` for downstream aggregation).
  const explicitThreadId =
    typeof sanitized["threadId"] === "string" && (sanitized["threadId"] as string).length > 0
      ? (sanitized["threadId"] as string)
      : typeof sanitized["conversationId"] === "string" && (sanitized["conversationId"] as string).length > 0
        ? (sanitized["conversationId"] as string)
        : null;

  return {
    _reportType: "sms",
    accountId: context.accountId,
    ...sanitized,
    ...(createdAtUtc ? { createdAtUtc } : {}),
    ...(sentTimeUtc ? { sentTimeUtc } : {}),
    ...(receivedTimeUtc ? { receivedTimeUtc } : {}),
    ...(explicitThreadId ? { threadId: explicitThreadId } : {}),
  };
}

// ============================================================================
// Story 35.4 — NEW transformers for /triggers.json + /integrations.json
// ============================================================================

/**
 * Wire-format shape for `/triggers.json` `triggers[i]` rows
 * (LeadReactor configurations).
 *
 * Loose `Record` typing because CTM's `/triggers` endpoint shape is
 * sparsely documented publicly — the helper validates the required
 * `id` + `name` fields at runtime and emits a structured-log event if
 * the response shape drifts.
 */
export type CtmTriggerApiRow = Record<string, unknown>;

/**
 * Transform a single row from `/triggers.json`. LeadReactor names are
 * user-defined free-text — high CSV-injection risk. The `sanitizeStrings`
 * walker (which calls `stripCsvInjectionPrefix` on every string-valued
 * field) handles this defensively.
 *
 * Timestamps (`created_at`, `updated_at`, `last_fired_at`) normalize to
 * UTC via `normalizeCtmTimestampToUtc` per Round 5 Edge Case 5.
 */
export function transformCtmTriggerRow(
  row: CtmTriggerApiRow,
  context: {
    readonly accountId: string;
  },
): Record<string, unknown> {
  const camelKeyed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (FORBIDDEN_SANITIZE_KEYS.has(key)) continue;
    camelKeyed[snakeToCamel(key)] = value;
  }
  const sanitized = sanitizeStrings(camelKeyed);

  const createdAtUtc = normalizeCtmTimestampToUtc(sanitized["createdAt"]);
  const updatedAtUtc = normalizeCtmTimestampToUtc(sanitized["updatedAt"]);
  const lastFiredAtUtc = normalizeCtmTimestampToUtc(sanitized["lastFiredAt"]);

  return {
    _reportType: "triggers",
    accountId: context.accountId,
    ...sanitized,
    ...(createdAtUtc ? { createdAtUtc } : {}),
    ...(updatedAtUtc ? { updatedAtUtc } : {}),
    ...(lastFiredAtUtc ? { lastFiredAtUtc } : {}),
  };
}

/**
 * Wire-format shape for `/integrations.json` `integrations[i]` rows
 * (3rd-party partner connections — Google Ads, HubSpot, Salesforce,
 * Zapier, etc.).
 */
export type CtmIntegrationApiRow = Record<string, unknown>;

/**
 * Transform a single row from `/integrations.json`. Partner names
 * (`Google Ads`, `HubSpot CRM`, `Zapier`) are vendor-controlled
 * strings, but `stripCsvInjectionPrefix` still fires defensively.
 *
 * Timestamps (`connected_at`, `last_sync_at`) normalize to UTC.
 */
export function transformCtmIntegrationRow(
  row: CtmIntegrationApiRow,
  context: {
    readonly accountId: string;
  },
): Record<string, unknown> {
  const camelKeyed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (FORBIDDEN_SANITIZE_KEYS.has(key)) continue;
    camelKeyed[snakeToCamel(key)] = value;
  }
  const sanitized = sanitizeStrings(camelKeyed);

  const connectedAtUtc = normalizeCtmTimestampToUtc(sanitized["connectedAt"]);
  const lastSyncAtUtc = normalizeCtmTimestampToUtc(sanitized["lastSyncAt"]);

  return {
    _reportType: "integrations",
    accountId: context.accountId,
    ...sanitized,
    ...(connectedAtUtc ? { connectedAtUtc } : {}),
    ...(lastSyncAtUtc ? { lastSyncAtUtc } : {}),
  };
}
